import { useState, useEffect } from "react";
import * as yup from "yup";
import * as React from "react";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import { AiOutlineSave } from "react-icons/ai";
import { BiCheckCircle } from "react-icons/bi";
import { HashLoader } from "react-spinners";
import MuiAlert from "@mui/material/Alert";

import { MainButton } from "../../components/MainButton";
import { COLORS } from "../../utils/colors";
import { createOrg } from "../../api/user";
import { useDispatch, useSelector } from "react-redux";
import { updateUser } from "../../store/mainReducer";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function Step1({
  step1Completed,
  setStep1Completed,
  supabase,
  handleSnackbar,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState("");
  const { session } = useSelector((state) => state.main);
  const validationSchema = yup.object({
    email: yup
      .string("Enter your email")
      .email("Enter a valid email")
      .required("Email required")
      .oneOf(
        [session?.user?.email],
        "Email must match the address used to log in"
      ),
    website: yup
      .string("Enter the website")
      .url("Please enter a valid URL in format https://example.com")
      .required("Company website required"),
    companyName: yup
      .string("Enter the company name")
      .min(3, "The company name should be of minimum 3 characters length")
      .required("Company name required"),
    contactPerson: yup
      .string("Enter the contact person")
      .min(4, "Name too short, minimum 4 characters required")
      .required("Contact person required"),
  });

  const formik = useFormik({
    initialValues: {
      email: session?.user?.email || "",
      website: "",
      companyName: "",
      contactPerson: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  useEffect(() => {
    formik.setFieldValue("email", session?.user?.email);
    formik.setFieldValue(
      "website",
      `https://${session?.user?.email.split("@")[1]}`
    );
    formik.setFieldValue(
      "companyName",
      `${session?.user?.email.split("@")[1].split(".")[0]}`
    );
  }, [session]);

  const handleselectedFile = (event) => {
    setLogo(event.target.files[0]);
  };

  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);

      /**
       * Show snackbar if user click submit without logo
       */
      if (!logo) {
        handleSnackbar("Uploading a logo is required", "error");
        setLoading(false);
        return;
      }

      const logoURL = await uploadImage(logo);

      const { data, error } = await createOrg({
        email: values.email,
        website: values.website,
        companyName: values.companyName,
        contact_person: values.contactPerson,
        logo: logoURL,
      });

      if (error) {
        handleSnackbar(error.message, "error");
        setLoading(false);
        return;
      }

      setStep1Completed(true);
      dispatch(updateUser(data));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error: ", error);
    }
  };

  const uploadImage = async (file) => {
    console.log("Uploading image...");

    try {
      const { data, error } = await supabase.storage
        .from("ooval-bucket")
        .upload(
          /**
           * Folder structure is organization_folders/{domain}
           */
          `organization_folders/${
            session?.user?.email.split("@")[1].split(".")[0]
          }/${session?.user?.email.split("@")[1].split(".")[0]}`,
          file,
          {
            upsert: true,
          }
        );

      if (error) {
        console.log(error);
        handleSnackbar(error.message, "error");
        return;
      }

      if (data) {
        const publicURL = await getPublicUrl();

        return publicURL;
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const getPublicUrl = async () => {
    const { data, error } = supabase.storage
      .from("ooval-bucket")
      .getPublicUrl(
        `organization_folders/${
          session?.user?.email.split("@")[1].split(".")[0]
        }/${session?.user?.email.split("@")[1].split(".")[0]}`
      );

    if (error) {
      console.log(error);
      return;
    }

    return data.publicUrl;
  };

  return (
    <div
      className="standard-container"
      style={{
        opacity: step1Completed ? 0.5 : 1,
        borderColor: step1Completed && COLORS.mainAccent,
      }}
    >
      <h2>
        Step 1/2 - Finalize your account{" "}
        {step1Completed && <BiCheckCircle color={COLORS.mainAccent} />}
      </h2>
      <p id="subTitle" style={{ marginBottom: "20px" }}>
        Fill in the information below to finalize your account.
      </p>
      <div className="field-container">
        <p
          id="subheaderBlack"
          style={{ margin: "0px 0px 3px", alignSelf: "center" }}
        >
          Company logo
        </p>
        <input
          type="file"
          disabled={loading || step1Completed}
          className="input-upload"
          onChange={handleselectedFile}
          placeholder="Upload logo"
        />
      </div>
      <form>
        <div className="two-columns-fields-container">
          <div className="field-container">
            <p
              id="subheaderBlack"
              style={{ margin: "0px 0px 3px", alignSelf: "center" }}
            >
              Email
            </p>
            <TextField
              fullWidth
              disabled
              size="small"
              className="input"
              id="email"
              name="email"
              placeholder="richie@hooli.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#f0f1f5",
                  },
                "& .MuiOutlinedInput-input:hover": {
                  cursor: "not-allowed",
                },
              }}
            />
          </div>
          <div className="field-container">
            <p
              id="subheaderBlack"
              style={{ margin: "0px 0px 3px", alignSelf: "center" }}
            >
              Company website
            </p>
            <TextField
              fullWidth
              disabled={loading || step1Completed}
              size="small"
              className="input"
              id="website"
              name="website"
              placeholder="https://hooli.com"
              value={formik.values.website}
              onChange={formik.handleChange}
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={formik.touched.website && formik.errors.website}
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#f0f1f5",
                  },
                "& .MuiOutlinedInput-input:hover": {
                  cursor: (loading || step1Completed) && "not-allowed",
                },
              }}
            />
          </div>
        </div>
        <div className="two-columns-fields-container">
          <div className="field-container">
            <p
              id="subheaderBlack"
              style={{ margin: "0px 0px 3px", alignSelf: "center" }}
            >
              Company name
            </p>
            <TextField
              fullWidth
              disabled={loading || step1Completed}
              size="small"
              className="input"
              id="companyName"
              name={"companyName"}
              placeholder={"Hooli Inc."}
              value={formik.values.companyName}
              onChange={formik.handleChange}
              error={
                formik.touched.companyName && Boolean(formik.errors.companyName)
              }
              helperText={
                formik.touched.companyName && formik.errors.companyName
              }
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#f0f1f5",
                  },
                "& .MuiOutlinedInput-input:hover": {
                  cursor: (loading || step1Completed) && "not-allowed",
                },
              }}
            />
          </div>
          <div className="field-container">
            <p
              id="subheaderBlack"
              style={{ margin: "0px 0px 3px", alignSelf: "center" }}
            >
              Contact person
            </p>
            <TextField
              fullWidth
              disabled={loading || step1Completed}
              size="small"
              className="input"
              name={"contactPerson"}
              placeholder={"Richard Hendricks"}
              value={formik.values.contactPerson}
              onChange={formik.handleChange}
              error={
                formik.touched.contactPerson &&
                Boolean(formik.errors.contactPerson)
              }
              helperText={
                formik.touched.contactPerson && formik.errors.contactPerson
              }
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "6px",
                  border: `2px solid #f0f1f5`,
                  transition: ".2s ease-in-out all",
                },
                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "#f0f1f5",
                  },
                "& .MuiOutlinedInput-input:hover": {
                  cursor: (loading || step1Completed) && "not-allowed",
                },
              }}
            />
          </div>
        </div>
        <div style={{ height: "10px" }} />
        <MainButton
          text={step1Completed ? "Account finalized" : "Save account details"}
          disabled={loading || step1Completed}
          color={COLORS.black}
          hoverColor={COLORS.black}
          textColor={COLORS.white}
          borderColor={COLORS.grey}
          shadowColor={COLORS.mainAccent}
          textHoverColor={COLORS.lightGrey}
          onClickAction={() => formik.handleSubmit()}
          width={"fit-content"}
        >
          {loading ? (
            <HashLoader size={20} color={COLORS.white} />
          ) : step1Completed ? (
            <BiCheckCircle size={20} color={COLORS.mainAccent} />
          ) : (
            <AiOutlineSave size={20} />
          )}
        </MainButton>
      </form>
    </div>
  );
}
